// Race HUD on top of the base HUD: start lights, lap counter and race time,
// tyre and battery meters, damage and weather icons, the engineer's radio
// strip, an on-demand strategy panel (tap the mini-map) and the pit entry hint.

import { COMPOUNDS, type Compound } from '../race/tyres';
import type { RadioMessage } from '../race/race';
import { formatTime } from './hud';

export interface RaceHudState {
  lightsLit: number;
  showLights: boolean;
  lap: number;
  laps: number;
  raceTime: number;
  penalties: number;
  compound: Compound;
  wear: number;
  /** Laps left on these tyres before the cliff, estimated. */
  tyreLapsLeft: number;
  damage: number;
  rain: number;
  wetness: number;
  lowGrip: boolean;
  forecast: Array<{ lap: number; rain: number }>;
  /** Gap to the best race at the same distance, s (negative = ahead). */
  ghostGap: number | undefined;
  pitHint: 'none' | 'approach' | 'box' | 'armed';
  pitLoss: number;
  ruleMet: boolean;
  pitLimiter: boolean;
}

export class RaceHud {
  private readonly root: HTMLElement;
  private readonly lights: HTMLElement;
  private readonly lightEls: HTMLElement[] = [];
  private readonly info: HTMLElement;
  private readonly tyreLetter: HTMLElement;
  private readonly tyreBar: HTMLElement;
  private readonly status: HTMLElement;
  private readonly radioEl: HTMLElement;
  private readonly panel: HTMLElement;
  private readonly pit: HTMLElement;
  onBox: (() => void) | undefined;
  private readonly queue: RadioMessage[] = [];
  private radioUntil = 0;
  private panelUntil = 0;
  private lastPanel = '';
  private lastInfo = '';
  private lastStatus = '';
  private lastTyre = '';
  onRadio: ((m: RadioMessage) => void) | undefined;

  constructor(hudRoot: HTMLElement, mapEl: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'race-hud';
    this.root.innerHTML = `
      <div class="race-lights">${'<span class="race-light"><i></i><i></i></span>'.repeat(5)}</div>
      <div class="race-info"></div>
      <div class="race-side">
        <div class="race-tyre"><span class="race-tyre-letter"></span><span class="race-tyre-wear"><i></i></span></div>
        <div class="race-status"></div>
      </div>
      <div class="race-radio"></div>
      <div class="race-panel"></div>
      <button class="race-pit" aria-label="Box this lap"><b>BOX</b><small>tap to pit</small></button>`;
    hudRoot.appendChild(this.root);
    const q = <T extends HTMLElement>(sel: string): T => this.root.querySelector(sel) as T;
    this.lights = q('.race-lights');
    this.lightEls = [...this.root.querySelectorAll<HTMLElement>('.race-light')];
    this.info = q('.race-info');
    this.tyreLetter = q('.race-tyre-letter');
    this.tyreBar = q('.race-tyre-wear i');
    this.status = q('.race-status');
    this.radioEl = q('.race-radio');
    this.panel = q('.race-panel');
    this.pit = q('.race-pit');
    this.pit.addEventListener('pointerdown', (e) => e.stopPropagation());
    this.pit.addEventListener('click', () => this.onBox?.());
    // The mini-map opens the strategy panel.
    mapEl.style.pointerEvents = 'auto';
    mapEl.addEventListener('click', () => {
      this.panelUntil = this.panelUntil > performance.now() ? 0 : performance.now() + 6000;
    });
    this.show(false);
  }

  show(on: boolean): void {
    this.root.style.display = on ? '' : 'none';
    if (!on) {
      this.queue.length = 0;
      this.radioEl.classList.remove('show');
    }
  }

  /** Queue engineer messages; each shows for a few seconds. */
  radio(messages: RadioMessage[]): void {
    for (const m of messages) {
      // Box calls jump the queue.
      if (m.kind === 'box' || m.kind === 'flag') this.queue.unshift(m);
      else this.queue.push(m);
    }
    if (this.queue.length > 4) this.queue.length = 4;
  }

  update(s: RaceHudState): void {
    const now = performance.now();
    this.lights.classList.toggle('show', s.showLights);
    this.lightEls.forEach((el, i) => el.classList.toggle('on', i < s.lightsLit));

    const pen = s.penalties > 0 ? ` <b class="race-pen">+${s.penalties}s</b>` : '';
    const gap = s.ghostGap === undefined ? '' : `<span class="race-gap ${s.ghostGap <= 0 ? 'ahead' : 'behind'}">${s.ghostGap <= 0 ? '−' : '+'}${Math.abs(s.ghostGap).toFixed(1)}</span>`;
    // Only touch the DOM when something visible changed (tenths are enough for the clock).
    const info = `<span class="race-lap">LAP ${Math.min(s.lap, s.laps)}/${s.laps}</span><span class="race-time">${formatTime(s.raceTime).slice(0, -2)}${pen}</span>${gap}`;
    if (info !== this.lastInfo) {
      this.info.innerHTML = info;
      this.lastInfo = info;
    }

    const spec = COMPOUNDS[s.compound];
    const life = Math.round(Math.max(0, 1 - s.wear) * 100);
    const tyreKey = `${s.compound}:${life}:${s.wear > 1 ? 2 : s.wear > 0.75 ? 1 : 0}`;
    if (tyreKey !== this.lastTyre) {
      this.lastTyre = tyreKey;
      this.tyreLetter.textContent = spec.letter;
      this.tyreLetter.style.borderColor = spec.color;
      this.tyreLetter.style.color = spec.color;
      this.tyreBar.style.width = `${life}%`;
      this.tyreBar.style.background = s.wear > 1 ? '#ff3b30' : s.wear > 0.75 ? '#ffcc33' : '#3ddc84';
    }

    const icons: string[] = [];
    if (s.damage > 0.02) icons.push(`<span class="race-icon dmg" style="opacity:${0.5 + s.damage / 2}">WING</span>`);
    if (s.rain > 0.05) icons.push(`<span class="race-icon rain">${s.rain > 0.6 ? 'RAIN' : 'DRIZZLE'}</span>`);
    else if (s.wetness > 0.1) icons.push(`<span class="race-icon wet">${s.wetness > 0.3 ? 'WET' : 'DAMP'}</span>`);
    if (s.lowGrip) icons.push('<span class="race-icon low">LOW GRIP</span>');
    if (s.pitLimiter) icons.push('<span class="race-icon pit">PIT LIMITER</span>');
    const status = icons.join('');
    if (status !== this.lastStatus) {
      this.status.innerHTML = status;
      this.lastStatus = status;
    }

    // Radio strip.
    if (now > this.radioUntil) {
      const next = this.queue.shift();
      if (next) {
        this.radioEl.textContent = next.text;
        this.radioEl.className = `race-radio show ${next.kind}`;
        this.radioUntil = now + Math.max(3200, next.text.length * 70);
        this.onRadio?.(next);
      } else {
        this.radioEl.classList.remove('show');
      }
    }

    // Strategy panel.
    const open = now < this.panelUntil;
    this.panel.classList.toggle('show', open);
    if (open) {
      const fc = s.forecast
        .map((f) => `<span class="fc ${f.rain > 0.5 ? 'heavy' : f.rain > 0.1 ? 'light' : 'dry'}" title="Lap ${f.lap}">${f.lap}</span>`)
        .join('');
      const html = `
        <div><b>Tyres</b> ${spec.name}, ${Math.round(s.wear * 100)}% worn, ~${Math.max(0, Math.round(s.tyreLapsLeft))} laps left</div>
        <div><b>Pit stop</b> costs ~${Math.round(s.pitLoss)} s${s.ruleMet ? '' : ' · <span class="race-warn">2nd dry compound still needed</span>'}</div>
        <div class="race-fc"><b>Forecast</b> ${fc}</div>
        <div class="race-track"><b>Track</b> ${s.wetness < 0.05 ? 'dry' : s.wetness < 0.2 ? 'damp' : s.wetness < 0.6 ? 'wet' : 'standing water'}</div>`;
      if (html !== this.lastPanel) {
        this.panel.innerHTML = html;
        this.lastPanel = html;
      }
    }

    this.pit.classList.toggle('show', s.pitHint !== 'none');
    this.pit.classList.toggle('box', s.pitHint === 'box');
    this.pit.classList.toggle('armed', s.pitHint === 'armed');
    const label = s.pitHint === 'armed' ? 'boxing this lap' : 'tap to pit';
    const small = this.pit.querySelector('small')!;
    if (small.textContent !== label) small.textContent = label;
  }
}
